import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, Grid, Divider, Chip, CircularProgress, Dialog, IconButton } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../contexts/AuthContext';
import LogTheme from './LogTheme';
import { getAuthHeaders, getImageDisplayUrl } from '../../utils/api';

const ShowServiceLog = () => {
  const { id } = useParams();
  const { userId } = useAuth();
  const [serviceLog, setServiceLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorizationError, setAuthorizationError] = useState(false);
  const [authorizationErrorMessage, setAuthorizationErrorMessage] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_SERVER_URL}/api/servicelog/${id}`, {
      headers: getAuthHeaders()
    })
      .then(response => {
        if (!response.ok) {
          if (response.status === 401) {
            console.error('401: Inte autentiserad. Logga in igen.');
            return;
          }
          if (response.status === 403) {
            return response.json().catch(() => ({})).then((data) => {
              setAuthorizationErrorMessage(data.message || 'Du har inte behörighet att se denna servicelogg.');
              setAuthorizationError(true);
              setLoading(false);
            });
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (!data) return; // Om response inte var ok, data är undefined
        console.log('Fetched data:', data);
        // Servern har redan godkänt åtkomst (ägare eller inbjuden med accessRole)
        if (typeof data.images === 'string') {
          data.images = JSON.parse(data.images);
        }
        setServiceLog(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error:', error);
        setAuthorizationError(true);
        setLoading(false);
      });
  }, [id, userId]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    if (serviceLog && serviceLog.images) {
      setLightboxIndex((prev) => (prev + 1) % serviceLog.images.length);
    }
  };

  const prevImage = () => {
    if (serviceLog && serviceLog.images) {
      setLightboxIndex((prev) => (prev - 1 + serviceLog.images.length) % serviceLog.images.length);
    }
  };

  if (loading) {
    return (
      <LogTheme>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </LogTheme>
    );
  }

  if (authorizationError) {
    return (
      <LogTheme>
        <Box padding={4}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="error" gutterBottom>
                {authorizationErrorMessage || 'Du har inte behörighet att se denna servicelogg.'}
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Loggbokens ägare kan uppgradera till Premium under Prenumerationsplaner för att se alla loggar.
              </Typography>
              <Button component={Link} to="/servicelogs" variant="contained" sx={{ mt: 1, mr: 1 }}>
                Tillbaka
              </Button>
              <Button component={Link} to="/subscription" variant="outlined" sx={{ mt: 1 }}>
                Prenumerationsplaner
              </Button>
            </CardContent>
          </Card>
        </Box>
      </LogTheme>
    );
  }

  if (!serviceLog) {
    return (
      <LogTheme>
        <Box padding={4}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="error">
                Serviceloggen kunde inte hämtas.
              </Typography>
              <Button component={Link} to="/servicelogs" variant="contained" sx={{ mt: 2 }}>
                Tillbaka
              </Button>
            </CardContent>
          </Card>
        </Box>
      </LogTheme>
    );
  }

  return (
    <LogTheme>
      <Box padding={{ xs: 2, md: 4 }} sx={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} className="no-print">
              <Button component={Link} to="/servicelogs" variant="outlined" startIcon={<NavigateBeforeIcon />}>
                Tillbaka
              </Button>
              <Button
                component={Link}
                to={`/updateservicelog/${id}`}
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
              >
                Ändra
              </Button>
            </Box>

            <Box mb={3}>
              <Typography variant="h1" component="h1" gutterBottom>
                {serviceLog.title}
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {new Date(serviceLog.date).toLocaleDateString('sv-SE')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Båtens namn/modell
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {serviceLog.boatNameModel || '-'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Motor/typ
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {serviceLog.engineType || '-'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Serviceperson eller företag
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {serviceLog.servicePersonOrCompany || '-'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Typ av service/underhåll
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {serviceLog.serviceType || '-'}
                  </Typography>
                </Box>
              </Grid>
              {serviceLog.engineService && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Motorservice
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {serviceLog.engineService}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {serviceLog.sailAndRig && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Segel och rigg
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {serviceLog.sailAndRig}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {serviceLog.electronicsAndNavigation && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Elektronik och navigationssystem
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {serviceLog.electronicsAndNavigation}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {serviceLog.hullAndDeck && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Skrov och däck
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {serviceLog.hullAndDeck}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {serviceLog.rudderPropellerAndDrive && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Roder, propeller, och drev
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {serviceLog.rudderPropellerAndDrive}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {serviceLog.fuelSystem && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Bränslesystem
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {serviceLog.fuelSystem}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {serviceLog.batteryAndElectricalSystem && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Batteri och elsystem
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {serviceLog.batteryAndElectricalSystem}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {serviceLog.sparePartsAndReplacements && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Reservdelar/byten
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {serviceLog.sparePartsAndReplacements}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {serviceLog.recommendedActions && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Rekommenderade åtgärder
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {serviceLog.recommendedActions}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {serviceLog.costAndTime && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Kostnad och tid
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {serviceLog.costAndTime}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {serviceLog.comments && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Kommentarer/Anmärkningar
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {serviceLog.comments}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {serviceLog.images && serviceLog.images.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h3" gutterBottom sx={{ mb: 3 }}>
                Bilder ({serviceLog.images.length})
              </Typography>
              <Grid container spacing={2}>
                {serviceLog.images.map((imageUrl, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: 4,
                          },
                        }}
                        onClick={() => openLightbox(index)}
                      >
                        <Box
                          component="img"
                          src={getImageDisplayUrl(imageUrl)}
                          alt={`Bild ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      </Card>
                    </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Lightbox Dialog */}
        <Dialog
          open={lightboxOpen}
          onClose={closeLightbox}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              color: 'white',
            },
          }}
        >
          <Box sx={{ position: 'relative', p: 2 }}>
            <IconButton
              onClick={closeLightbox}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'white',
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </IconButton>
            {serviceLog.images && serviceLog.images.length > 1 && (
              <>
                <IconButton
                  onClick={prevImage}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'white',
                    zIndex: 1,
                  }}
                >
                  <NavigateBeforeIcon fontSize="large" />
                </IconButton>
                <IconButton
                  onClick={nextImage}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'white',
                    zIndex: 1,
                  }}
                >
                  <NavigateNextIcon fontSize="large" />
                </IconButton>
              </>
            )}
            {serviceLog.images && serviceLog.images[lightboxIndex] && (
              <Box
                component="img"
                src={getImageDisplayUrl(serviceLog.images[lightboxIndex])}
                alt={`Bild ${lightboxIndex + 1}`}
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            )}
            {serviceLog.images && serviceLog.images.length > 1 && (
              <Typography
                variant="body2"
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: 'white',
                }}
              >
                {lightboxIndex + 1} / {serviceLog.images.length}
              </Typography>
            )}
          </Box>
        </Dialog>
      </Box>
    </LogTheme>
  );
};

export default ShowServiceLog;